import { faroCoreApiClient } from "@api/faro-core-api-client";
import { AddressAutocomplete } from "@components/address-autocomplete/address-autocomplete";
import { STACK_ERROR_SX } from "@components/common/dialog/dialog-constants";
import { FaroSelect } from "@components/common/faro-select/faro-select";
import { GroupAutoComplete } from "@components/common/group-autocomplete/group-autocomplete";
import { LabelWithHelp } from "@components/common/label-with-help";
import { MemberAutocomplete } from "@components/common/member-autocomplete/member-autocomplete";
import { getErrorDisplayMarkup } from "@context-providers/error-boundary/error-boundary-utils";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { Alert, Button, Dialog, FormHelperErrorText, MenuItem, TextField } from "@flat-ui";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { useResolveProjectManagerIdentity } from "@hooks/projects/use-resolve-project-manager-identity";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Box, Stack } from "@mui-wrapper";
import { useCompanyId } from "@context-providers/company-id-provider";
import { ConvertUtils, UiUtils } from "@stellar/utils";
import { addCreatedProject } from "@store/projects-map/projects-map-slice";
import { addProject } from "@store/projects/projects-slice";
import { convertProjectTypesToSdbProject } from "@store/projects/projects-slice-utils";
import { preferredAreaUnitSelector } from "@store/settings/settings-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { formatAreaUnit } from "@utils/data-display";
import { validateNumber } from "@utils/math-utils";
import { getProjectAddressType } from "@utils/project-utils";
import { parseCoordinatesString } from "@utils/string-utils";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useMemo, useState } from "react";
const DEFAULT_ERRORS = {
  name: null,
  selectedGroupId: null,
  selectedProjectManagerIdentity: null,
  area: null,
  clientName: null,
  projectDescription: null
};

/** Defines the minimum area in sqft that we allow for the project area. */
const MINIMUM_PROJECT_AREA = 1;
/** Renders the create project button and the functionality in projects page */
export function CreateProjectDialog({
  isDemo = false
}) {
  const [selectedProjectManager, setSelectedProjectManager] = useState(null);
  const dispatch = useAppDispatch();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    isScreenSmOrSmaller
  } = useMediaQueryList();
  const companyId = useCompanyId();
  const {
    trackEvent
  } = useTrackEvent();
  const {
    navigateToProjectDetail
  } = useAppNavigation();
  const {
    canCreateProjects
  } = useHasUserValidFeatureCompanyLevel();
  const {
    canAssignManagers,
    resolveProjectManagerIdentity
  } = useResolveProjectManagerIdentity();
  const preferredAreaUnit = useAppSelector(preferredAreaUnitSelector);
  const currentUser = useAppSelector(currentUserSelector);
  // A project manager of the workspace can only create projects they manage themselves
  const isProjectManagerOnly = currentUser?.role === "PROJECT_MANAGER";
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const lastPage = 1;
  const [groupMembers, setGroupMembers] = useState([]);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState(undefined);
  /** Stores the raw input of the area before validation and parsing. */
  const [area, setArea] = useState("");
  /** Stores the error message of the area if there is one, null otherwise. */
  const [areaErrorMessage, setAreaErrorMessage] = useState(null);
  /** Stores the parsed version of the area, only updates when valid values are input */
  const [areaParsed, setAreaParsed] = useState(MINIMUM_PROJECT_AREA);
  const [areaUnit, setAreaUnit] = useState(preferredAreaUnit);
  const [clientName, setClientName] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);
  const [position, setPosition] = useState(undefined);
  const [postalAddress, setPostalAddress] = useState();
  const [geocodingAddressState, setGeocodingAddressState] = useState("uninitialized");
  const isGeocodingAddress = geocodingAddressState === "pending";

  /**
   * The project manager identity to send to the backend. When the workspace cannot assign
   * managers, the manager selection is hidden and the current user (main admin) is used.
   */
  const projectManagerIdentity = isProjectManagerOnly ? currentUser?.identity ?? null : resolveProjectManagerIdentity(selectedProjectManager);
  const [errors, setErrors] = useState(DEFAULT_ERRORS);

  /** Evaluates whether the project area text field has a valid input. */
  const isAreaValid = useMemo(() => {
    return areaErrorMessage === null;
  }, [areaErrorMessage]);

  /**
   * Evaluates whether the confirm button should be disabled because there are errors in the dialog.
   */
  const isConfirmDisabled = useMemo(() => {
    // The first page is never disabled because the fields are validated
    // when the user clicks on next.
    if (page === 0) {
      return false;
    }

    // For the second page, disable the confirm button if the input area value is invalid or
    // if the address is being geocoded, to prevent the user from creating the project before
    // the address GPS position is obtained.
    return !isAreaValid || isGeocodingAddress;
  }, [page, isAreaValid, isGeocodingAddress]);

  /** Whether the back button should be shown in the dialog. */
  const shouldShowBackButton = page > 0 && lastPage > 0;

  // eslint-disable-next-line @typescript-eslint/require-await -- Please review lint error
  async function onOpenDialog() {
    trackEvent({
      name: ProjectEvents.startCreateNewProject
    });
    setName("");
    setAddress("");
    setSelectedGroupId(undefined);
    setSelectedProjectManager(null);
    setClientName("");
    setArea("");
    setAreaErrorMessage(null);
    setAreaParsed(MINIMUM_PROJECT_AREA);
    setAreaUnit(preferredAreaUnit);
    setProjectDescription("");
    setPage(0);
    setErrorMessage(null);
    setErrors(DEFAULT_ERRORS);
    setIsDialogOpen(true);
  }

  /**
   * Triggered every time the user inputs text in the project area
   * text fields. It sets the raw value of the area that is displayed on the text field
   * but it also stores the parsed value if it is valid or the error message if not.
   */
  function onAreaChanged(changedArea) {
    setArea(changedArea);
    const validatedArea = validateNumber({
      attrName: "Area",
      // Since area is optional we can allow the user to leave the field empty
      // and in that case we use the minimum value for the area.
      // what we don't allow is setting the value to 0.
      value: changedArea === "" ? 1 : changedArea,
      minOrEqual: 1,
      shouldAllowDecimal: false
    });
    if (validatedArea.isValid) {
      setAreaErrorMessage(null);
      setAreaParsed(validatedArea.parsedNumber);
    } else {
      setAreaErrorMessage(validatedArea.errorMessage);
      setAreaParsed(MINIMUM_PROJECT_AREA);
    }
  }

  /**
   * Triggered every time a group is selected.
   * It fetches the members that belong to that group and stores them
   * in the members variable.
   *
   * @param groupId Id of the group to fetch its members from.
   * @returns The fetched members for the provided group, otherwise undefined.
   */
  async function onGroupSelected(groupId) {
    if (!groupId) {
      setSelectedGroupId(undefined);
      setSelectedProjectManager(null);
      return;
    }
    setSelectedGroupId(groupId);
    // When the workspace cannot assign managers the project manager selection is hidden,
    // so there is no need to fetch the group members (which would otherwise briefly block
    // the user while loading).
    if (!canAssignManagers) {
      return;
    }
    // As soon as you select a different group, empty the members array.
    // TODO: This should be moved to the group slice as createAsyncThunk: https://faro01.atlassian.net/browse/ST-698
    setGroupMembers([]);
    try {
      const {
        members
      } = await faroCoreApiClient.groups.getGroupById({
        companyId,
        groupId
      });
      setGroupMembers(members ?? []);
      setSelectedProjectManager(getDefaultProjectManager(members ?? []));
      return members;
    } catch (error) {
      handleErrorWithToast({
        id: `getGroupById-${Date.now().toString()}`,
        title: "Cannot get the group details",
        error
      });
    }
  }

  /**
   * Returns the project manager to preselect once a group is selected: the current user when they
   * can only manage their own projects, or else its only member. A project manager selected for
   * another group is not kept.
   */
  function getDefaultProjectManager(members_0) {
    if (isProjectManagerOnly) {
      return members_0.find(({
        identity
      }) => identity === currentUser?.identity) ?? null;
    }
    return members_0.length === 1 ? members_0[0] : null;
  }
  function setSingleError(errorKey, errorValue) {
    setErrors(current => {
      return {
        ...current,
        [errorKey]: errorValue
      };
    });
  }

  /**
   * poor man's validation until we have a standardized validation technique
   *
   * @param pageNumber which page to validate
   * @returns a boolean that indicates whether the form is valid or not
   */
  function validateForm(pageNumber) {
    // first clear all current errors
    setErrors(DEFAULT_ERRORS);

    // next, validate one by one
    let hasNoErrors = true;
    if (pageNumber === 0) {
      if (!name) {
        setSingleError("name", "Project name is required!");
        hasNoErrors = false;
      }
      if (!selectedGroupId) {
        setSingleError("selectedGroupId", "Project group is required!");
        hasNoErrors = false;
      }
      if (canAssignManagers && !projectManagerIdentity) {
        setSingleError("selectedProjectManagerIdentity", "Project manager is required!");
        hasNoErrors = false;
      }
    }

    // why do we need to manually set a variable "hasNoErrors" instead of checking the errors (by looping for example)?
    // because of react's queuing system. setState is not immediate, but we need to know if there is an error
    // immediately in the function that uses this function without using useEffect
    return hasNoErrors;
  }

  /**
   * Sends a request to the backend to create a project.
   * If the promise is resolved it means that the project was successfully created,
   * otherwise an error is thrown.
   */
  async function createProject() {
    // Theoretically the user should not be able to reach this state
    // if the project manager was not provided but do this for type safety
    // and as an edge case if our members selection didn't work properly.
    if (!projectManagerIdentity) {
      throw new Error("Project manager is required!");
    }
    const sqft = areaUnit === "sqft" ? areaParsed : ConvertUtils.sqMeterToSqFoot(areaParsed);
    const project = await faroCoreApiClient.projects.createNewProject({
      companyId,
      payload: {
        name,
        // We allow the user to leave the address empty, but the backend requires an non-empty string,
        // so we use a DASH character as an accepted workaround to indicate that the address is empty.
        address: address.trim() || UiUtils.DASH,
        clientName,
        description: projectDescription,
        sqft,
        groupId: String(selectedGroupId),
        projectManagerIdentity,
        position,
        postalAddress
      }
    });

    // Add SdbProject entity to both projects and map projects redux slices.
    const sdbProject = convertProjectTypesToSdbProject(project);
    dispatch(addProject(sdbProject));
    dispatch(addCreatedProject(sdbProject));
    navigateToProjectDetail({
      companyId,
      projectId: project.id
    });
  }
  function trackConfirm() {
    if (page !== lastPage) {
      return;
    }
    const eventProps = {
      descriptionLength: projectDescription?.length ?? 0,
      projectNameLength: name.length,
      addressLength: address.length,
      clientNameLength: clientName.length,
      area: areaParsed ?? 0,
      addressType: getProjectAddressType({
        address,
        postalAddress,
        emptyAddressType: "none"
      }),
      isGpsPositionSet: Boolean(position)
    };
    trackEvent({
      name: ProjectEvents.createNewProject,
      props: eventProps
    });
  }

  /** Submit groups to be invited */
  async function handleConfirm() {
    trackConfirm();

    // If the form is not valid have an early exit
    if (!validateForm(page)) {
      return;
    }
    if (page < lastPage) {
      // If we are not in the last page, the confirm button is only used to go to the next page.
      setPage(page + 1);
      return;
    }
    setIsLoading(true);
    try {
      await createProject();
      // If the project has been created the dialog can be closed.
      setIsDialogOpen(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error_0) {
      // If the project creation failed, do not close the dialog and
      // show an error message inside the dialog.
      const errorMessage_0 = getErrorDisplayMarkup(error_0);
      setErrorMessage(errorMessage_0);
    } finally {
      // Regardless whether it succeeded or fail to create a project, the loading spinner
      // should be hidden and the user should be able to initiate a new request to create
      // a project or manually close the dialog
      setIsLoading(false);
    }
  }

  /**
   * Handles setting a custom address field.
   * If the new value is a valid coordinates string, it sets the position to
   * that value, otherwise it sets the position to undefined.
   * @param input The new address input value
   */
  function onAddressInputChange(input) {
    setAddress(input);
    const trimmedInput = input.trim();
    const position_0 = parseCoordinatesString(trimmedInput);
    setPosition(position_0 ?? undefined);
    // For a custom address we must set the postal address value as undefined so it's not populated in the backend.
    setPostalAddress(undefined);
  }

  /**
   * Handles the selection of a geocoded address. Sets address, position and postal address.
   * @param geocodedAddress The selected geocoded address
   */
  function onGeocodedAddressSelect(geocodedAddress) {
    setAddress(geocodedAddress.address);
    setPosition(geocodedAddress.position);
    setPostalAddress(geocodedAddress.postalAddress);
  }

  /**
   * We should not render create project button
   * if the feature to create projects in company context isn't enabled and permitted
   * That means either the company does not have the feature to create projects
   * Or the current user has no permission to create projects.
   */
  if (!canCreateProjects) {
    return null;
  }
  return <>
      {/* Button to open the dialog */}
      <Button onClick={onOpenDialog} sx={{
      width: isScreenSmOrSmaller && !isDemo ? "100%" : "auto"
    }}>
        New project
      </Button>

      <Dialog title={page === 0 ? "Create new project" : "Project details"} open={isDialogOpen} confirmText={page === lastPage ? "Create" : "Next"} isConfirmDisabled={isConfirmDisabled} onConfirm={handleConfirm} showSpinner={isLoading} onClose={() => setIsDialogOpen(false)} onCancel={() => setIsDialogOpen(false)} secondaryText={shouldShowBackButton ? "Back" : undefined} onSecondary={shouldShowBackButton ? () => setPage(page - 1) : undefined} showXButton={false}>
        {/* First page: Required fields */}
        {page === 0 && <>
            <Stack sx={STACK_ERROR_SX}>
              <LabelWithHelp title="Project name" isRequired />
              <TextField autoFocus fullWidth text={name} onTextChanged={setName} error={errors.name} placeholder="Enter the project name..." />
            </Stack>
            <Stack sx={STACK_ERROR_SX}>
              <LabelWithHelp title="Group" help={"Groups are created to allow for a more flexible structure and ensure " + "better management of members and projects."} isRequired />

              <GroupAutoComplete callbackOnGroupSelected={onGroupSelected} selectedGroupId={selectedGroupId} />

              {errors.selectedGroupId && <FormHelperErrorText>
                  {errors.selectedGroupId}
                </FormHelperErrorText>}
            </Stack>
            {canAssignManagers && <Stack sx={STACK_ERROR_SX}>
                <LabelWithHelp title="Project manager" help={"Choose a project manager to manage this project. This person can edit " + "permissions, invite new members and also can archive, download and remove " + "their related projects."} isDisabled={!selectedGroupId} isRequired />
                <MemberAutocomplete members={groupMembers} selectedMember={selectedProjectManager} onOptionChange={setSelectedProjectManager} isDisabled={!selectedGroupId || isProjectManagerOnly} placeholder="Choose who can manage this project..." />

                {errors.selectedProjectManagerIdentity && <FormHelperErrorText>
                    {errors.selectedProjectManagerIdentity}
                  </FormHelperErrorText>}
              </Stack>}
          </>}

        {/* Second page: Optional fields */}
        {page === 1 && <>
            <Stack sx={STACK_ERROR_SX}>
              <LabelWithHelp title="Client name" help="The client is the person or organization for whom this project is carried out." />
              <TextField autoFocus={true} fullWidth text={clientName} onTextChanged={setClientName} placeholder="Enter the client name..." error={errors.clientName} />
            </Stack>
            <Stack sx={STACK_ERROR_SX}>
              <LabelWithHelp title="Address" />
              <AddressAutocomplete input={address} setInput={onAddressInputChange} shouldAutoFocus={false} placeholder="Example: street, region, country" onGeocodedAddressSelect={onGeocodedAddressSelect} onLoadingGeocodedAddressChange={setGeocodingAddressState} />
            </Stack>
            <Stack>
              <LabelWithHelp title="Project area" help={"A project area is the amount of square feet/meter that is assigned " + "to a project. This value must be equal to or greater than 1."} />
            </Stack>
            <Box component="div" sx={{
          ...STACK_ERROR_SX,
          display: "flex",
          alignItems: "top",
          width: "100%"
        }}>
              <Box component="div" sx={{
            width: "100%"
          }}>
                <TextField text={area}
            // Using type="number" brought some undesired behavior like not working
            // properly on Firefox or showing the buttons to increase/decrease value
            // that are useless in this case. Therefore we use our own validation.
            type="text" autoFocus={false} onTextChanged={onAreaChanged} placeholder="Enter project area..." fullWidth inputProps={{
              min: 0
            }} controlStyle={{
              flexGrow: 1
            }} sx={{
              // The field is joined to the unit dropdown on its right.
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0
            }} error={errors.area || areaErrorMessage || undefined} />
              </Box>
              <Box component="div">
                <FaroSelect value={areaUnit} variant="outlined" isSplitInput={true} onChange={setAreaUnit}>
                  <MenuItem value="sqft">{formatAreaUnit("sqft")}</MenuItem>
                  <MenuItem value="sqm">{formatAreaUnit("sqm")}</MenuItem>
                </FaroSelect>
              </Box>
            </Box>
            <Stack sx={STACK_ERROR_SX}>
              <LabelWithHelp title="Project description" />
              <TextField text={projectDescription} multiline minRows={4} autoFocus={false} fullWidth onTextChanged={setProjectDescription} placeholder="Enter project description..." error={errors.projectDescription} />
            </Stack>
            {errorMessage && <Stack sx={STACK_ERROR_SX}>
                <Alert variant="error" title={errorMessage} />
              </Stack>}
          </>}
      </Dialog>
    </>;
}